import React, { useEffect, useRef, useState } from 'react';

import { Icon, Link, Image, Button, XLineIcon, ModalCustom } from '@/components';
import {
  PinnedProductTypes,
  LiveStreamStatus,
  CollectionStickerType,
  MessageDataSocket,
  Lottery,
  ConversationType,
  MessageData,
  Comment,
} from '@/features/livestream/detail/const';
import {
  useCreateChatConversation,
  useCreateCodeTicketQuery,
  useTicketByUserQuery,
} from '@/features/livestream/detail/api';
import { TFunction } from 'next-i18next';
import { PagePath, LotteryGameStatus, LotterySocketMethod, LiveProductMethod } from '@/layouts/shared';
import { useProfile } from '@/contexts/profile';
import Comments from './Comments';
import { SendCommentBlock } from './Comments/SendCommentBlock';
import { PinnedProduct } from './Comments/PinProduct';
import { useFetchConversationByProductQuery } from '@/features/products/detail/api';
import { getProductImage } from '@/libs/app';
import { useCreateOrderChat } from '@/features/consults/chat/api';

export type ChatPartialProps = {
  liveStreamId: number;
  pinnedProduct: PinnedProductTypes | undefined;
  isAuth: boolean;
  status: string;
  t: TFunction;
  collectionStickers: CollectionStickerType[];
  retry: boolean;
  messageData: MessageDataSocket;
  showLotteryBtn: boolean;
  hideLotteryBtn: () => void;
  lotteryGame: Lottery | null;
  commentList: (MessageData | Comment)[];
  defaultOffset: number;
  products: PinnedProductTypes[];
};
export type IOrderDeposit = {
  productImage: string | undefined;
  fullname: string | undefined;
  productCode: string | undefined;
  isShow: boolean;
  id: number;
};
const ChatPartial = (props: ChatPartialProps) => {
  const {
    liveStreamId,
    status,
    pinnedProduct,
    isAuth,
    t,
    collectionStickers,
    messageData,
    retry,
    showLotteryBtn,
    hideLotteryBtn,
    lotteryGame,
    commentList,
    defaultOffset,
  } = props;
  const LOTTERY_URL = process.env.LOTTERY_URL;
  const profile: any = useProfile();
  const userId = profile?.id || null;
  const commentRef = useRef<any>(null);
  const sendCommentRef = useRef<any>(null);
  const [orderDeposit, setOrderDeposit] = useState<IOrderDeposit | undefined>();
  const [ticket, setTicket] = useState<number>();
  const [winnerAuction, setWinnerAuction] = useState<{
    fullName: string;
    productCode: string;
  }>();
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [orderId, setOrderId] = useState<number>();
  const [gameStatus, setGameStatus] = useState<string>(lotteryGame?.status || LotteryGameStatus.Ended);
  const [gameId, setGameId] = useState<string | null>(lotteryGame?.id || null);
  const [myReward, setMyReward] = useState<string>('');
  const [showProducts, setShowProducts] = useState<boolean>(true);
  const { refetch: refetchTicketByUser } = useTicketByUserQuery(
    {
      lottery_game_id: gameId || '',
    },
    {
      enabled: false,
      retry: false,
      onSuccess: ({ data }) => setTicket(data.number),
      onError: () => setTicket(undefined),
    }
  );
  const { mutate: sendMessage } = useCreateOrderChat({});
  const { mutate, isLoading: isCreateChatConversationLoading } = useCreateChatConversation({
    onSuccess: ({ data }) => {
      const { conversation } = data;
      sendMessage({
        id: conversation.id,
        data: {
          content: `TV ${conversation.type_code}`,
          images: [],
          type: 'text',
        },
      });
    },
    onError: ({ message }) => {},
  });
  const { refetch: fetchConversation, isLoading: isFetchConversationLoading } = useFetchConversationByProductQuery(
    pinnedProduct?.product_id || 0,
    {
      retry: 0,
      enabled: false,
      onSuccess: (data) => {
        const { id, type_code, type } = data.data || {};
        sendMessage({
          id: id,
          data: {
            content: `TV ${type_code}`,
            images: [],
            type: 'text',
          },
        });
      },
      onError: () => {
        return mutate({
          title: pinnedProduct?.products.product_name || '',
          type_id: pinnedProduct?.product_id || 0,
          type_image: getProductImage(pinnedProduct?.products.images) || '',
          type_code: pinnedProduct?.products.product_code || '',
          type: ConversationType.Product,
        });
      },
    }
  );
  const setEndLottery = () => {
    setTimeout(() => {
      setGameStatus(LotteryGameStatus.Ended);
      setTicket(undefined);
    }, 10000);
  };
  const setProductOrder = (code: string, image: string, fullName: string, id: number) => {
    setTimeout(() => {
      setOrderDeposit({
        productCode: code,
        productImage: image,
        fullname: fullName,
        isShow: true,
        id: id,
      });
    }, 500);
  };
  useEffect(() => {
    if (gameId && showLotteryBtn && !ticket) refetchTicketByUser();
  }, [gameId, showLotteryBtn, ticket, refetchTicketByUser]);
  useEffect(() => {
    if (messageData?.method === LotterySocketMethod.UserConfirmTicket) {
      if (messageData?.data.users?.id === userId) refetchTicketByUser();
    }
    if (messageData?.method === LotterySocketMethod.OpenLottery) {
      setGameStatus(LotteryGameStatus.Pending);
      setGameId(messageData.data.lottery_game_id || null);
    }
    if (messageData?.method === LotterySocketMethod.EndLottery) {
      setEndLottery();
    }
    if (messageData?.method === LotterySocketMethod.AdminPickTicketLottery) {
      if (messageData?.data.users?.id === userId) {
        setShowPopup(true);
        setMyReward(messageData.data.prize?.name || '');
      }
    }
    if (messageData?.method === LiveProductMethod.CreateOrder) {
      if (messageData?.data.product?.id && messageData?.data.product?.id === pinnedProduct?.product_id) {
        const dataOrder = messageData?.data;
        setOrderId(dataOrder.product?.id);
        setProductOrder(
          pinnedProduct?.products.product_code,
          dataOrder?.product?.image || '',
          dataOrder?.users?.fullname || '',
          dataOrder?.product?.id || 0
        );
      }
    }
    if (messageData?.method === LiveProductMethod.AuctionEnded) {
      if (messageData.data.winner) {
        const winner = messageData.data.winner;
        setWinnerAuction({
          fullName: winner.fullname,
          productCode: messageData.data.product_code || '',
        });
      }
    }
  }, [messageData, pinnedProduct, refetchTicketByUser, userId]);

  useEffect(() => {
    if (pinnedProduct?.product_id !== orderDeposit?.id) {
      setOrderDeposit(undefined);
    }
  }, [orderDeposit?.id, pinnedProduct?.product_id]);
  const { mutate: mutateCreateCode, isLoading: loadingCreateCode } = useCreateCodeTicketQuery({
    onSuccess: ({ data: { code } }) => {
      if (!code) return;
      const newWindow = window.open(`${LOTTERY_URL}?code=${encodeURIComponent(code)}`, '_blank');
      if (newWindow) newWindow.opener = null;
    },
  });

  const onConsultClick = () => {
    if (pinnedProduct) {
      setIsDisabled(true);
      sendCommentRef.current &&
        sendCommentRef.current.sendComment({
          comment: `TV ${pinnedProduct.products.product_code}`,
          user_id: userId || 0,
          live_stream_id: +liveStreamId,
          images: null,
        });
      return fetchConversation();
    }
  };
  const directPlayGame = () => {
    if (gameId && gameStatus === LotteryGameStatus.Pending) mutateCreateCode({ lottery_game_id: gameId });
  };
  const sendComment = (code: string) => {
    if (code) {
      setIsDisabled(true);
      sendCommentRef.current &&
        sendCommentRef.current.sendComment({
          comment: `TV ${code}`,
          user_id: userId || 0,
          live_stream_id: +liveStreamId,
          images: null,
        });
    }
  };
  return (
    <>
      <div className="tw-bg-muted-90 tw-rounded-5 tw-h-80-screen tw-flex tw-flex-col tw-gap-y-4 tw-p-4 tw-justify-between">
        {showLotteryBtn ? (
          <>
            {gameStatus !== LotteryGameStatus.Ended && (
              <div className="tw-flex tw-bg-white tw-rounded-5 tw-px-4 tw-pt-2 tw-pb-4 tw-gap-x-2 tw-relative">
                <Image src="/confetti-flat.gif" alt="confetti" className="tw-w-16 tw-h-16"></Image>
                {ticket ? (
                  <div className="tw-flex-1 tw-text-primary-main">
                    {t('detail.label.joined-lottery')}: {ticket}
                  </div>
                ) : (
                  <div className="tw-flex-1">
                    <span className="tw-text-primary-main">{t('detail.label.invite-join-lottery')}</span>
                    <div className="tw-flex tw-justify-end tw-mt-4 tw-w-full">
                      <Button
                        disabled={loadingCreateCode || gameStatus !== LotteryGameStatus.Pending}
                        className="tw-bg-primary-main tw-text-xs tw-uppercase tw-text-white tw-py-1 tw-px-6 tw-rounded !tw-w-max"
                        onClick={directPlayGame}
                      >
                        {t('layout:btn.join')}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="tw-absolute tw-right-2 tw-cursor-pointer" onClick={hideLotteryBtn}>
                  <Icon icon={<XLineIcon />} className="tw-w-4 tw-h-4 tw-text-secondary-70" />
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {pinnedProduct && (
              <PinnedProduct
                t={t}
                product={pinnedProduct?.products}
                productId={pinnedProduct?.product_id}
                isDisabled={isDisabled}
                orderProductId={orderId}
                userId={userId}
                liveStreamId={liveStreamId}
                onConsultClick={onConsultClick}
              />
            )}
          </>
        )}

        <Comments
          ref={commentRef}
          isAuth={isAuth}
          liveId={liveStreamId}
          retry={retry}
          messageData={messageData}
          commentList={commentList}
          defaultOffset={defaultOffset}
          isShowProducts={showProducts}
          sendComment={sendComment}
          hiddenProducts={() => setShowProducts(false)}
          t={t}
        />
        {status === LiveStreamStatus.LIVE_NOW && pinnedProduct && orderDeposit && orderDeposit.isShow && (
          <div className="tw-gap-y-2">
            <>
              <div
                className="tw-p-2 tw-flex tw-rounded tw-items-center a-flip-left"
                style={{
                  backgroundColor: '#EDFFFC',
                }}
              >
                {orderDeposit.productImage && (
                  <Image className="tw-h-8 tw-w-8" alt={orderDeposit.productCode} src={orderDeposit.productImage} />
                )}
                <span
                  className="tw-font-light tw-flex tw-flex-1 tw-ml-2"
                  style={{
                    color: '#11695A',
                    fontSize: '13px',
                  }}
                >
                  Quý khách {orderDeposit.fullname} đã cọc thành công {orderDeposit.productCode}
                </span>
              </div>
            </>
          </div>
        )}
        {winnerAuction && (
          <div className="tw-gap-y-2">
            <>
              <div
                className="tw-p-2 tw-flex tw-rounded tw-items-center a-flip-left"
                style={{
                  backgroundColor: '#FFF7E3',
                }}
              >
                <span
                  className="tw-font-light tw-flex tw-flex-1 tw-ml-2"
                  style={{
                    color: '#A54400',
                    fontSize: '13px',
                  }}
                >
                  Quý khách {winnerAuction.fullName} đã đấu giá thành công {winnerAuction.productCode}
                </span>
              </div>
            </>
          </div>
        )}
        <SendCommentBlock
          ref={sendCommentRef}
          t={t}
          liveStreamId={liveStreamId}
          status={status}
          collectionStickers={collectionStickers}
          setDisabled={() => setIsDisabled(false)}
          isAuth={isAuth}
          userId={userId}
          onShowProducts={() => setShowProducts((prev) => !prev)}
        />
      </div>
      {showPopup && (
        <ModalCustom
          className="tw-rounded-2xl tw-overflow-hidden tw-w-60 md:tw-w-96 tw-flex tw-items-center tw-flex-col"
          childrenClass="!tw-gap-y-0 tw-w-full tw-items-center"
        >
          <Image src="/confetti-flat.gif" alt="confetti" className="tw-w-52 tw-h-52"></Image>
          <div className="tw-mt-2 tw-text-center">Chúc mừng bạn đã chiến thắng</div>
          <div className="tw-mt-1 tw-text-center">{myReward}</div>
          <div className="tw-w-full">
            <Button
              className="tw-bg-primary-main tw-text-white tw-mt-4 tw-w-full tw-rounded-full"
              onClick={() => setShowPopup(false)}
            >
              OK
            </Button>
          </div>
        </ModalCustom>
      )}
    </>
  );
};

export default ChatPartial;
